import {
	AdvancedSearchType,
	FilterNameTypes,
	ListViewMap,
	customFieldEntityMap,
	filterNameMap,
	fixOrderFilters
} from 'App/helpers/advancedSearchHelper';
import { ListView, ListViewFilter } from 'App/resources/AllIWant';
import RequestBuilder, { Sort, comparisonTypes } from 'Resources/RequestBuilder';
import type { AnyAction } from 'redux';
import { AppThunk } from '../index';
import type Client from 'App/resources/Model/Client';
import type Contact from 'App/resources/Model/Contact';
import type Activity from 'App/resources/Model/Activity';
import type Appointment from 'App/resources/Model/Appointment';
import type Opportunity from 'App/resources/Model/Opportunity';
import type Order from 'App/resources/Model/Order';
import type { EntityFilter } from 'App/pages/AdvancedSearch/AdvancedSearchEntities/EntityFilter';
import { CancelablePromise, makeCancelable } from '@upsales/components/Utils/CancelablePromise';
import {
	getCustomConfig,
	getFieldWithPrefix,
	isCustom,
	isCustomCategory,
	isInactiveValue,
	parseFilters
} from 'App/helpers/filterHelper';
import categoryFilterBaseConfig from 'App/babel/filters/CategoryFilterConfig';
import ClientResource from 'App/resources/Client';
import ActivityResource from 'Resources/Activity';
// App/resources/Contact, not the deprecated Resources/Contact: the deprecated one runs responses
// through genericParser with babel/attributes/ContactAttributes, which strips every field that map
// doesn't define (projects, categories, segments, ...) — breaking columns auto-added from filters (WEB-10438)
import ContactResource from 'App/resources/Contact';
import OrderResource from 'App/resources/Order';
import AppointmentResource from 'Resources/Appointment';
import { type FilterConfig } from 'App/babel/filterConfigs/FilterConfig';
import logError from 'Helpers/logError';
import clientFilters from 'App/babel/filterConfigs/Client';
import contactFilters from 'App/babel/filterConfigs/Contact';
import orderFilters from 'App/babel/filterConfigs/Order';
import Resource from 'Resources/Resource';
import UserRoleTree from 'Components/Helpers/UserRoleTree';
import { getFeatureAvailableFromState } from 'App/components/hooks/featureHelper';
import { findAllChildren } from 'App/babel/angular/attributes/filter/treeHelpers';
import { Feature } from 'Store/actions/FeatureHelperActions';
import AppService from 'App/legacy/AppService';

export const RESET = '[AdvancedSearch] RESET';
export const MULTI_SETTER = '[AdvancedSearch] MULTI_SETTER';
export const SET_SELECTED_VIEW = '[AdvancedSearch] SET_SELECTED_VIEW';
export const SET_TYPE = '[AdvancedSearch] SET_TYPE';
export const SET_RESULT_TYPE = '[AdvancedSearch] SET_RESULT_TYPE';
export const SET_COLUMNS = '[AdvancedSearch] SET_COLUMNS';
export const SET_SORTING = '[AdvancedSearch] SET_SORTING';
export const SET_RESULTS = '[AdvancedSearch] SET_RESULTS';
export const SET_NUM_RESULTS = '[AdvancedSearch] SET_NUM_RESULTS';
export const SET_FETCHING_RESULTS = '[AdvancedSearch] SET_FETCHING_RESULTS';
export const SET_FILTERS = '[AdvancedSearch] SET_FILTERS';
export const SET_FILTERS_PARTIAL_BY_NAME = '[AdvancedSearch] SET_FILTERS_PARTIAL_BY_NAME';
export const SET_RESULTS_EXPANDED = '[AdvancedSearch] SET_RESULTS_EXPANDED';
export const SET_OFFSET = '[AdvancedSearch] SET_OFFSET';
export const SET_VIEW_LOADING = '[AdvancedSearch] SET_VIEW_LOADING';

const activityStatusRawConfig = {
	type: 'raw',
	field: 'closeDate',
	build: (filter: any, rb: RequestBuilder, getField: (field: string) => { field: string }) => {
		if (filter.value === null) {
			return;
		} else if (filter.value === true) {
			rb.addFilter(getField('closeDate'), comparisonTypes.NotEquals, null);
		} else {
			rb.addFilter(getField('closeDate'), comparisonTypes.Equals, null);
		}
	}
} as unknown as FilterConfig;

const appointmentStatusRawConfig = {
	type: 'raw',
	field: 'endDate',
	build: (filter: any, rb: RequestBuilder, getField: (field: string) => { field: string }) => {
		if (filter.value === null) {
			return;
		} else if (filter.value === 'closed') {
			rb.addFilter(getField('endDate'), comparisonTypes.LowerThanEquals, new Date().toISOString());
		} else {
			rb.addFilter(getField('endDate'), comparisonTypes.GreaterThanEquals, new Date().toISOString());
		}
	}
} as unknown as FilterConfig;

const activityTypeListConfig = {
	type: 'list',
	field: 'activityType.id'
} as unknown as FilterConfig;

const statusRawConfig = {
	type: 'raw',
	field: 'probability',
	build: (filter: any, rb: RequestBuilder, getField: (field: string) => { field: string }) => {
		if (!filter.inactive) {
			if (filter.value === 'active') {
				rb.addFilter(getField('probability'), comparisonTypes.GreaterThan, 0);
			} else if (filter.value === 'lost') {
				rb.addFilter(getField('probability'), comparisonTypes.Equals, 0);
			}
		}
	}
} as unknown as FilterConfig;

const agreementIdRawConfig = {
	type: 'raw',
	field: 'agreement.id',
	build: (filter: any, rb: RequestBuilder, getField: (field: string) => { field: string }) => {
		if (!filter.inactive) {
			if (filter.value === 'yes') {
				rb.addFilter(getField('agreement.id'), comparisonTypes.GreaterThan, 0);
			} else if (filter.value === 'no') {
				rb.addFilter(getField('agreement.id'), comparisonTypes.Equals, null);
			}
		}
	}
} as unknown as FilterConfig;

const fromOpportunityRawConfig = {
	type: 'raw',
	field: 'fromOpportunity',
	build: (filter: any, rb: RequestBuilder, getField: (field: string) => { field: string }) => {
		if (!filter.inactive) {
			if (filter.value === 'yes') {
				rb.addFilter(getField('fromOpportunity'), comparisonTypes.Equals, true);
			} else if (filter.value === 'no') {
				rb.addFilter(getField('fromOpportunity'), comparisonTypes.Equals, false);
			}
		}
	}
} as unknown as FilterConfig;

const subAccountsRawConfig = {
	type: 'raw',
	field: 'operationalAccount.id',
	build: (filter: any, rb: RequestBuilder, getField: (field: string) => { field: string }) => {
		if (!filter.inactive && filter.value === 'hide') {
			rb.addFilter(getField('operationalAccount.id'), comparisonTypes.Equals, null);
		}
	}
} as unknown as FilterConfig;

const advancedDateRawConfig = {
	type: 'raw',
	field: 'date',
	build: (filter: any, rb: RequestBuilder, getField: (field: string) => { field: string }) => {
		const val = filter.value;
		if (!val) return;
		const allowedFields = ['date', 'regDate', 'closeDate'];
		const field = allowedFields.includes(val.field) ? val.field : 'date';
		if (val.start) {
			const start = new Date(val.start);
			start.setHours(0, 0, 0, 0);
			rb.addFilter(getField(field), comparisonTypes.GreaterThanEquals, start.toISOString());
		}
		if (val.end) {
			const end = new Date(val.end);
			end.setHours(23, 59, 59, 999);
			rb.addFilter(getField(field), comparisonTypes.LowerThanEquals, end.toISOString());
		}
	}
} as unknown as FilterConfig;

const unsubscribedRawConfig = {
	type: 'raw',
	field: 'unsubscribed',
	build: (filter: any, rb: RequestBuilder, getField: (field: string) => { field: string }) => {
		if (!filter.inactive) {
			if (filter.value === 'yes') {
				rb.addFilter(getField('unsubscribed'), comparisonTypes.NotEquals, null);
			} else if (filter.value === 'no') {
				rb.addFilter(getField('unsubscribed'), comparisonTypes.Equals, null);
			}
		}
	}
} as unknown as FilterConfig;

const entityFieldPrefix: Record<AdvancedSearchType, string> = {
	[AdvancedSearchType.accounts]: 'client',
	[AdvancedSearchType.contacts]: 'contact',
	[AdvancedSearchType.activities]: 'activity',
	[AdvancedSearchType.appointments]: 'appointment',
	[AdvancedSearchType.opportunities]: 'order',
	[AdvancedSearchType.orders]: 'order'
};

const filterNameAliases: Record<string, string> = {
	Website: 'Webpage',
	ParentId: 'Parent',
	OrgNoLookup: 'OrgNo'
};

let _cachedClientFilters: { [key: string]: FilterConfig } | null = null;
let _cachedContactFilters: { [key: string]: FilterConfig } | null = null;
let _cachedOrderFilters: { [key: string]: FilterConfig } | null = null;
let _cachedActivityFilters: { [key: string]: FilterConfig } | null = null;
let _cachedAppointmentFilters: { [key: string]: FilterConfig } | null = null;

const getAngularFilterConfig = (name: string, type: AdvancedSearchType): FilterConfig | undefined => {
	const angularName = filterNameAliases[name] || name;
	if (!_cachedClientFilters) _cachedClientFilters = clientFilters();
	switch (type) {
		case AdvancedSearchType.accounts:
			return _cachedClientFilters[angularName];
		case AdvancedSearchType.contacts:
			if (!_cachedContactFilters) _cachedContactFilters = contactFilters();
			return _cachedContactFilters[angularName] || _cachedClientFilters[angularName];
		case AdvancedSearchType.opportunities:
		case AdvancedSearchType.orders:
			if (!_cachedOrderFilters) _cachedOrderFilters = orderFilters();
			return _cachedOrderFilters[angularName];
		case AdvancedSearchType.activities: {
			if (!_cachedActivityFilters) _cachedActivityFilters = require('App/babel/filterConfigs/Activity').default();
			return _cachedActivityFilters![angularName];
		}
		case AdvancedSearchType.appointments: {
			if (!_cachedAppointmentFilters)
				_cachedAppointmentFilters = require('App/babel/filterConfigs/Appointment').default();
			return _cachedAppointmentFilters![angularName];
		}
		default:
			return undefined;
	}
};

const customFieldEntityType: Partial<Record<AdvancedSearchType, string>> = {
	[AdvancedSearchType.activities]: 'task',
	[AdvancedSearchType.appointments]: 'task'
};

const getEntityFilterConfig = (
	name: string,
	type: AdvancedSearchType,
	customFields: any[]
): FilterConfig | undefined => {
	if (isCustom(name)) {
		return getCustomConfig(name, customFields, customFieldEntityType[type] || type);
	}
	if (name === 'StandardCategory' || isCustomCategory(name)) {
		return categoryFilterBaseConfig as unknown as FilterConfig;
	}
	if (name === 'Status' && (type === AdvancedSearchType.opportunities || type === AdvancedSearchType.orders)) {
		return statusRawConfig;
	}
	if (name === 'Status' && type === AdvancedSearchType.activities) {
		return activityStatusRawConfig;
	}
	if (name === 'Status' && type === AdvancedSearchType.appointments) {
		return appointmentStatusRawConfig;
	}
	if (name === 'ActivityType' && type === AdvancedSearchType.activities) {
		return activityTypeListConfig;
	}
	if (name === 'AdvancedDate') return advancedDateRawConfig;
	if (name === 'AgreementId') return agreementIdRawConfig;
	if (name === 'FromOpportunity') return fromOpportunityRawConfig;
	if (name === 'SubAccounts') return subAccountsRawConfig;
	if (name === 'Unsubscribed') return unsubscribedRawConfig;
	const angularConfig = getAngularFilterConfig(name, type);
	if (angularConfig) return angularConfig as FilterConfig;
	if (name === 'Id' || name === 'id') {
		return { type: 'text', field: 'id' } as unknown as FilterConfig;
	}
	return undefined;
};

export type AdvancedSearchState = Readonly<{
	selectedView: ListView | null;
	type: AdvancedSearchType;
	resultType: AdvancedSearchType;
	columns: string[];
	sorting: Sort[];
	results: {
		accounts: Client[];
		contacts: Contact[];
		activities: Activity[];
		appointments: Appointment[];
		opportunities: Opportunity[];
		orders: Order[];
	};
	numResults: {
		accounts: number;
		contacts: number;
		activities: number;
		appointments: number;
		opportunities: number;
		orders: number;
	};
	fetchingResults: {
		accounts: boolean;
		contacts: boolean;
		activities: boolean;
		appointments: boolean;
		opportunities: boolean;
		orders: boolean;
	};
	filters: EntityFilter;
	resultsExpanded: boolean;
	offset: number;
	viewLoading: boolean;
}>;

export const getDefaultFilters = (type?: AdvancedSearchType, accountSelf?: any): EntityFilter => ({
	ACCOUNT:
		type === AdvancedSearchType.accounts && getFeatureAvailableFromState(accountSelf, Feature.INACTIVE_COMPANIES)
			? { Active: { filterName: 'Active', value: true, inactive: false } }
			: {},
	CONTACT:
		type === AdvancedSearchType.contacts ? { Active: { filterName: 'Active', value: true, inactive: false } } : {},
	ACTIVITY: {},
	APPOINTMENT: {},
	OPPORTUNITY: {
		IsOpportunity: { filterName: 'IsOpportunity', value: true, inactive: false }
	},
	ORDER: {
		IsOpportunity: { filterName: 'IsOpportunity', value: false, inactive: false }
	},
	ORDERROW: {}
});

export const initialState: AdvancedSearchState = {
	selectedView: null,
	type: AdvancedSearchType.accounts,
	resultType: AdvancedSearchType.accounts,
	columns: [],
	sorting: [],
	results: {
		accounts: [],
		contacts: [],
		activities: [],
		appointments: [],
		opportunities: [],
		orders: []
	},
	numResults: {
		accounts: 0,
		contacts: 0,
		activities: 0,
		appointments: 0,
		opportunities: 0,
		orders: 0
	},
	fetchingResults: {
		accounts: false,
		contacts: false,
		activities: false,
		appointments: false,
		opportunities: false,
		orders: false
	},
	filters: getDefaultFilters(),
	resultsExpanded: false,
	offset: 0,
	viewLoading: false
};

const defaultColumns = {
	[AdvancedSearchType.accounts]: ['name', 'users', 'Address_Visit_address'],
	[AdvancedSearchType.contacts]: ['name', 'title', 'client'],
	[AdvancedSearchType.activities]: ['date', 'client', 'description', 'contacts', 'users', 'activityType'],
	[AdvancedSearchType.appointments]: ['date', 'client', 'description', 'contact', 'users', 'activityType'],
	[AdvancedSearchType.opportunities]: ['date', 'client', 'description', 'value', 'stage'],
	[AdvancedSearchType.orders]: ['date', 'client', 'description', 'value', 'stage']
} satisfies Record<AdvancedSearchType, string[]>;

const defaultSorting = {
	[AdvancedSearchType.accounts]: [{ attribute: 'name', ascending: true }],
	[AdvancedSearchType.contacts]: [{ attribute: 'name', ascending: true }],
	[AdvancedSearchType.activities]: [{ attribute: 'date', ascending: false }],
	[AdvancedSearchType.appointments]: [{ attribute: 'date', ascending: false }],
	[AdvancedSearchType.opportunities]: [{ attribute: 'date', ascending: false }],
	[AdvancedSearchType.orders]: [{ attribute: 'date', ascending: false }]
} satisfies Record<AdvancedSearchType, Sort[]>;

const entityToResource: { [key in AdvancedSearchType]: Resource } = {
	[AdvancedSearchType.accounts]: ClientResource,
	[AdvancedSearchType.contacts]: ContactResource,
	[AdvancedSearchType.activities]: ActivityResource,
	[AdvancedSearchType.appointments]: AppointmentResource,
	[AdvancedSearchType.opportunities]: OrderResource,
	[AdvancedSearchType.orders]: OrderResource
};

const entityFilterModifiers: {
	[key in AdvancedSearchType]?: (rb: RequestBuilder, getField?: (field: string) => { field: string }) => void;
} = {
	[AdvancedSearchType.accounts]: (rb, getField) => {
		rb.addFilter(getField ? getField('isExternal') : { field: 'isExternal' }, comparisonTypes.Equals, 0);
	},
	[AdvancedSearchType.opportunities]: (rb, getField) => {
		rb.addFilter(getField ? getField('probability') : { field: 'probability' }, comparisonTypes.LessThan, 100);
	},
	[AdvancedSearchType.orders]: (rb, getField) => {
		rb.addFilter(getField ? getField('probability') : { field: 'probability' }, comparisonTypes.Equals, 100);
	}
};

const entityBaseFilters: {
	[key in AdvancedSearchType]?: (rb: RequestBuilder, getField: (field: string) => { field: string }) => void;
} = {
	[AdvancedSearchType.activities]: (rb, getField) => {
		rb.addFilter(getField('id'), comparisonTypes.NotEquals, null);
	},
	[AdvancedSearchType.appointments]: (rb, getField) => {
		rb.addFilter(getField('id'), comparisonTypes.NotEquals, null);
	}
};

let fetchTimeout: NodeJS.Timeout | null = null;
let fetchPromise: CancelablePromise<any> | null = null;
let countPromises: CancelablePromise<any>[] = [];
const FETCH_DEBOUNCE_TIME = 500;
export const PAGE_LIMIT = 50;

const addCrossEntityAccountFilters = (
	rb: RequestBuilder,
	type: AdvancedSearchType,
	resultType: AdvancedSearchType,
	filters: AdvancedSearchState['filters'],
	appCustomFields: Record<string, any>
) => {
	if (type !== AdvancedSearchType.accounts && Object.keys(filters.ACCOUNT || {}).length > 0) {
		const accountCustomFields = appCustomFields[customFieldEntityMap[AdvancedSearchType.accounts]] || [];
		const clientPrefix = resultType !== AdvancedSearchType.accounts ? 'client' : null;
		parseFilters(filters.ACCOUNT, 'ACCOUNT', rb, clientPrefix, {
			getConfig: name => getEntityFilterConfig(name, AdvancedSearchType.accounts, accountCustomFields) ?? null
		});
	}
};

const findUsersInTree = (tree: any[], roleIds: number[], selectAllChildren: boolean, output: number[]) => {
	for (const node of tree) {
		if (node.isRole && node.children?.length) {
			if (roleIds.includes(node.$id) || selectAllChildren) {
				findUsersInTree(node.children, roleIds, true, output);
			} else {
				findUsersInTree(node.children, roleIds, false, output);
			}
		} else if (!node.isRole && selectAllChildren) {
			output.push(node.id);
		}
	}
};

const expandRoleValues = (values: (string | number)[]): number[] => {
	const stringValues = values.filter((v): v is string => typeof v === 'string');
	const numericValues = values.filter((v): v is number => typeof v !== 'string');
	if (stringValues.length === 0) return numericValues;

	const roleIds = stringValues.filter(v => v.startsWith('role-')).map(v => parseInt(v.split('-')[1], 10));
	const numericStringIds = stringValues
		.filter(v => v !== 'self' && !v.startsWith('role-'))
		.map(v => parseInt(v, 10))
		.filter(n => !isNaN(n));

	if (stringValues.includes('self')) {
		numericValues.push(AppService.getSelf().id);
	}

	if (roleIds.length === 0) return [...numericValues, ...numericStringIds];

	const tree = UserRoleTree({ accessType: null, selectableRoles: true, roleIdPrefix: true });
	const expandedUserIds: number[] = [];
	findUsersInTree(tree, roleIds, false, expandedUserIds);
	return [...numericValues, ...numericStringIds, ...expandedUserIds];
};

const fixRoleFilters = (filters: Record<string, any>): Record<string, any> => {
	const fixed = { ...filters };

	if (Array.isArray(fixed.User?.value) && fixed.User.value.length) {
		fixed.User = { ...fixed.User, value: expandRoleValues(fixed.User.value) };
	}

	if (
		Array.isArray(fixed.AccountManagerFilters?.value?.User?.value) &&
		fixed.AccountManagerFilters.value.User.value.length
	) {
		fixed.AccountManagerFilters = {
			...fixed.AccountManagerFilters,
			value: {
				...fixed.AccountManagerFilters.value,
				User: {
					...fixed.AccountManagerFilters.value.User,
					value: expandRoleValues(fixed.AccountManagerFilters.value.User.value)
				}
			}
		};
	}

	return fixed;
};

const setColumnsFromFilters = (): AppThunk => (dispatch, getState) => {
	const { resultType, type, filters, columns } = getState().AdvancedSearch;
	const customFields = getState().App.customFields[customFieldEntityMap[type]] || [];
	const filterName = filterNameMap[resultType];
	const entityFilters = filters[filterName] || {};

	// Start with current columns (from view or defaults)
	const baseColumns = columns.length ? [...columns] : [...defaultColumns[resultType]];

	for (const [name, filter] of Object.entries(entityFilters)) {
		const config = getEntityFilterConfig(name, resultType, customFields);
		const columnPath = (config as any)?.columnPath;
		if (columnPath && config && !isInactiveValue(filter, config)) {
			baseColumns.push(columnPath);
		}
	}

	// Deduplicate and cap at 8
	const newColumns = [...new Set(baseColumns)].slice(0, 8);

	// Only dispatch if columns actually changed
	if (newColumns.length !== columns.length || newColumns.some((c, i) => c !== columns[i])) {
		dispatch({ type: SET_COLUMNS, columns: newColumns });
	}
};

/**
 * Shared filter-building logic used by fetchData, count queries, and buildCurrentRequestBuilder.
 * Builds a RequestBuilder with entity filters, account cross-filters, orderrow filters, and modifiers.
 */
const buildFiltersForResultType = (
	rt: AdvancedSearchType,
	type: AdvancedSearchType,
	filters: EntityFilter,
	customFields: any[],
	appCustomFields: Record<string, any[]>
): RequestBuilder => {
	const filterName = filterNameMap[type];
	const fixedEntityFilters = fixRoleFilters(filters[filterName]);
	if (fixedEntityFilters.Id?.value && !Array.isArray(fixedEntityFilters.Id.value)) {
		fixedEntityFilters.Id = { ...fixedEntityFilters.Id, value: [fixedEntityFilters.Id.value] };
	}
	// Match Angular's fixTitleFilter: contact Title uses list matching with exact comparison
	if (type === AdvancedSearchType.contacts && fixedEntityFilters.Title) {
		fixedEntityFilters.Title = { ...fixedEntityFilters.Title, comparisonType: 'MatchExact', forceType: 'list' };
	}
	const prefix = rt !== type ? entityFieldPrefix[type] : null;
	const rb = parseFilters(fixedEntityFilters, filterName, null, prefix, {
		getConfig: name => getEntityFilterConfig(name, type, customFields) ?? null
	});

	if (rt === type) {
		entityBaseFilters[type]?.(rb, getFieldWithPrefix(prefix));
	}

	const fixedFilters = { ...filters, ACCOUNT: fixRoleFilters(filters.ACCOUNT || {}) };
	addCrossEntityAccountFilters(rb, type, rt, fixedFilters, appCustomFields);

	if (
		(type === AdvancedSearchType.opportunities || type === AdvancedSearchType.orders) &&
		Object.keys(filters.ORDERROW || {}).length > 0
	) {
		const orderrowFilters = { ...filters.ORDERROW };

		// Expand selected product categories to include all child categories
		const categoryValues = orderrowFilters.OrderRowProductCategory?.value;
		if (Array.isArray(categoryValues) && categoryValues.length > 0) {
			const allCategories = AppService.getProductCategories();
			const allCategoryIds = [...new Set([...categoryValues, ...findAllChildren(allCategories, categoryValues)])];
			orderrowFilters.OrderRowProductCategory = {
				...orderrowFilters.OrderRowProductCategory,
				value: allCategoryIds
			};
		}

		const orderrowCustomFields = appCustomFields.orderrow || [];
		const orderrowPrefix = rt !== type ? 'order.orderRow' : 'orderRow';
		parseFilters(orderrowFilters, 'ORDERROW', rb, orderrowPrefix, {
			getConfig: name => getCustomConfig(name, orderrowCustomFields)
		});
	}

	entityFilterModifiers[rt]?.(rb);
	if (rt !== type && entityFilterModifiers[type]) {
		entityFilterModifiers[type]!(rb, getFieldWithPrefix(entityFieldPrefix[type]));
	}

	if (type !== AdvancedSearchType.accounts && rt === AdvancedSearchType.accounts) {
		rb.addExtraParam('groupChilds', true);
	}

	return rb;
};

const fetchData = (): AppThunk => (dispatch, getState) => {
	dispatch(setColumnsFromFilters());
	if (fetchTimeout) {
		clearTimeout(fetchTimeout);
		fetchTimeout = null;
	}
	fetchPromise?.cancel();
	countPromises.forEach(p => p.cancel());
	countPromises = [];
	const { resultType, sorting, type, filters } = getState().AdvancedSearch;
	const customFields = getState().App.customFields[customFieldEntityMap[type]] || [];
	const appCustomFields = getState().App.customFields;
	const resource = entityToResource[resultType];

	const rb = buildFiltersForResultType(resultType, type, filters, customFields, appCustomFields);

	if (sorting.length) {
		rb.addSort(sorting[0].attribute, sorting[0].ascending);
	}
	rb.limit = PAGE_LIMIT;
	rb.offset = getState().AdvancedSearch.offset;

	fetchPromise = makeCancelable(resource.find(rb.build()));

	fetchPromise.promise
		.then(({ data, metadata }) => {
			const { results, resultType, numResults } = getState().AdvancedSearch;
			dispatch({
				type: SET_NUM_RESULTS,
				numResults: { ...numResults, [AdvancedSearchType[resultType]]: metadata.total }
			});
			dispatch({ type: SET_RESULTS, results: { ...results, [AdvancedSearchType[resultType]]: data } });
		})
		.catch((err: Error) => {
			logError(err);
		})
		.finally(() => {
			const { resultType, fetchingResults } = getState().AdvancedSearch;
			dispatch({
				type: SET_FETCHING_RESULTS,
				fetchingResults: { ...fetchingResults, [AdvancedSearchType[resultType]]: false }
			});
			dispatch({ type: SET_VIEW_LOADING, viewLoading: false });
		});

	const resultTypes =
		type === AdvancedSearchType.accounts ? [AdvancedSearchType.accounts] : [type, AdvancedSearchType.accounts];

	for (const rt of resultTypes) {
		if (rt === resultType) continue;

		const countRb = buildFiltersForResultType(rt, type, filters, customFields, appCustomFields);
		countRb.limit = 0;
		countRb.fields = ['id'];

		const countResource = entityToResource[rt];
		const countPromise = makeCancelable(countResource.find(countRb.build()));
		countPromises.push(countPromise);
		countPromise.promise
			.then(({ metadata }: { metadata: { total: number } }) => {
				const { numResults } = getState().AdvancedSearch;
				dispatch({
					type: SET_NUM_RESULTS,
					numResults: { ...numResults, [AdvancedSearchType[rt]]: metadata.total }
				});
			})
			.catch(() => {});
	}

	return fetchPromise;
};

const fetchDataDebounced = (): AppThunk => dispatch => {
	if (fetchTimeout) {
		clearTimeout(fetchTimeout);
		fetchTimeout = null;
		fetchPromise?.cancel();
	}
	fetchTimeout = setTimeout(() => {
		dispatch(fetchData());
	}, FETCH_DEBOUNCE_TIME);
};

export const fetchResults =
	(debounceFetch = false, silent = false): AppThunk =>
	(dispatch, getState) => {
		if (!silent) {
			const { fetchingResults, resultType } = getState().AdvancedSearch;
			dispatch({
				type: SET_FETCHING_RESULTS,
				fetchingResults: { ...fetchingResults, [AdvancedSearchType[resultType]]: true }
			});
		}
		if (debounceFetch) {
			dispatch(fetchDataDebounced());
		} else {
			dispatch(fetchData());
		}
	};
export const setFilters =
	(filters: AdvancedSearchState['filters'], debounceFetch = false): AppThunk =>
	dispatch => {
		dispatch({ type: SET_OFFSET, offset: 0 });
		dispatch({ type: SET_FILTERS, filters });
		dispatch(fetchResults(debounceFetch));
	};

export const setPartialFilterByName =
	(
		filterNameType: FilterNameTypes,
		filterName: string,
		filter: Partial<Optional<ListViewFilter, 'filterName'>>,
		debounceFetch = false
	): AppThunk =>
	dispatch => {
		dispatch({ type: SET_OFFSET, offset: 0 });
		dispatch({ type: SET_FILTERS_PARTIAL_BY_NAME, filter, filterName, filterNameType });
		dispatch(fetchResults(debounceFetch));
	};

export const setSorting =
	(sorting: AdvancedSearchState['sorting']): AppThunk =>
	dispatch => {
		dispatch({ type: SET_OFFSET, offset: 0 });
		dispatch({ type: SET_SORTING, sorting });
		dispatch(fetchResults());
	};

export const setPage =
	(offset: number): AppThunk =>
	dispatch => {
		dispatch({ type: SET_OFFSET, offset });
		dispatch(fetchResults());
	};

export const setSelectedView =
	(selectedView: ListView | null): AppThunk =>
	(dispatch, getState) => {
		const { type } = getState().AdvancedSearch;
		const accountSelf = getState().App.accountSelf;
		dispatch({ type: SET_SELECTED_VIEW, selectedView });

		if (!selectedView || !selectedView.filters?.length) {
			dispatch(setFilters(getDefaultFilters(type, accountSelf)));
			return;
		}

		const [firstFilter] = selectedView.filters;
		if (firstFilter?.value && firstFilter?.filterName === 'advancedSearch') {
			const filters = fixOrderFilters(firstFilter.value as EntityFilter);

			dispatch(setFilters(filters));
		} else {
			dispatch(setFilters(getDefaultFilters(type, accountSelf)));
		}
	};

export const setResultType =
	(resultType: AdvancedSearchState['resultType']): AppThunk =>
	(dispatch, getState) => {
		const { selectedView, type, resultType: prevResultType } = getState().AdvancedSearch;
		if (prevResultType === resultType) {
			return;
		}
		if (resultType === type && selectedView) {
			dispatch({
				type: MULTI_SETTER,
				resultType,
				columns: selectedView.columns,
				sorting: selectedView.sorting,
				offset: 0
			});
		} else {
			// If type was changed to a sub search type we want to set columns and sorting to the default columns and sorting for that type
			dispatch({
				type: MULTI_SETTER,
				resultType,
				columns: defaultColumns[resultType],
				sorting: defaultSorting[resultType],
				offset: 0
			});
		}
		dispatch(fetchResults());
	};

// This will "reset" the state to work with another type
export const setType =
	(type: AdvancedSearchState['type']): AppThunk =>
	(dispatch, getState) => {
		const App = getState().App;
		const availableViews = App.listViews[ListViewMap[type]] || [];
		// Match Angular findViewByIdentity priority: default+private > default+editable > default > first
		const viewToSelect =
			availableViews.find(v => v.default && v.private) ||
			availableViews.find(v => v.default && v.editable) ||
			availableViews.find(v => v.default) ||
			availableViews[0] ||
			null;

		dispatch({ type: SET_TYPE, newType: type });
		dispatch({ type: SET_VIEW_LOADING, viewLoading: true });
		// We don't want to use setResultType here since this resets columns and sorting (setSelectedView will set these)
		dispatch({
			type: MULTI_SETTER,
			resultsExpanded: false,
			resultType: type,
			results: { ...initialState.results },
			numResults: { ...initialState.numResults },
			offset: 0
		});
		dispatch(setSelectedView(viewToSelect));
	};
export const buildCurrentRequestBuilder = (): AppThunk<RequestBuilder> => (_dispatch, getState) => {
	const { resultType, type, filters } = getState().AdvancedSearch;
	const customFields = getState().App.customFields[customFieldEntityMap[type]] || [];
	return buildFiltersForResultType(resultType, type, filters, customFields, getState().App.customFields);
};

const ACTION_HANDLERS: { [k: string]: (state: AdvancedSearchState, a: AnyAction) => AdvancedSearchState } = {
	[RESET]: () => ({ ...initialState }),
	[MULTI_SETTER]: (state, { type, ...updates }) => ({ ...state, ...updates }),
	[SET_SELECTED_VIEW]: (state, { selectedView }) => ({
		...state,
		selectedView,
		columns: selectedView?.columns ?? defaultColumns[state.type],
		sorting: selectedView?.sorting ?? defaultSorting[state.type]
	}),
	[SET_TYPE]: (state, { newType }) => ({ ...state, type: newType }),
	[SET_RESULT_TYPE]: (state, { resultType }) => ({ ...state, resultType }),
	[SET_COLUMNS]: (state, { columns }) => ({ ...state, columns }),
	[SET_SORTING]: (state, { sorting }) => ({ ...state, sorting }),
	[SET_RESULTS]: (state, { results }) => ({ ...state, results }),
	[SET_NUM_RESULTS]: (state, { numResults }) => ({ ...state, numResults }),
	[SET_FETCHING_RESULTS]: (state, { fetchingResults }) => ({ ...state, fetchingResults }),
	[SET_FILTERS]: (state, { filters }) => ({ ...state, filters }),
	[SET_FILTERS_PARTIAL_BY_NAME]: (state, { filter, filterName, filterNameType }) => ({
		...state,
		filters: {
			...state.filters,
			[filterNameType]: {
				...state.filters[filterNameType as FilterNameTypes],
				[filterName]: {
					...state.filters[filterNameType as FilterNameTypes][filterName],
					...filter
				}
			}
		}
	}),
	[SET_OFFSET]: (state, { offset }) => ({ ...state, offset }),
	[SET_RESULTS_EXPANDED]: (state, { resultsExpanded }) => ({ ...state, resultsExpanded }),
	[SET_VIEW_LOADING]: (state, { viewLoading }) => ({ ...state, viewLoading })
};

const AdvancedSearchReducer = (state = initialState, action: AnyAction) => {
	const handler = ACTION_HANDLERS[action.type];
	return handler ? handler(state, action) : state;
};

export default AdvancedSearchReducer;
